import { TriageResult, CaptureSource, SessionIntent, TriggerSourceFilter } from '../api'
import type { CaptureSummary } from '../api'

export const MANUAL_SENTINEL = '__manual__'

export interface HashState {
  nets: Set<string>
  triage: Set<TriageResult>
  sessionIntent: Set<SessionIntent>
  // Default is every bucket (no narrowing); `none` = no trigger source recorded.
  trigger: Set<TriggerSourceFilter>
  // Label ids; empty = no narrowing.
  labels: Set<string>
  // YYYY-MM-DD (local calendar days), null = unbounded.
  dateFrom: string | null
  dateTo: string | null
  captureId: string | null
  isLegacy: boolean
}

const DEFAULT_TRIAGE = (): Set<TriageResult> =>
  new Set([TriageResult.Keep, TriageResult.Pending])

// Default is "no filtering" — every bucket included.
const DEFAULT_SESSION_INTENT = (): Set<SessionIntent> =>
  new Set([SessionIntent.Fishing, SessionIntent.Testing, SessionIntent.Unknown])

const DEFAULT_TRIGGER = (): Set<TriggerSourceFilter> =>
  new Set([TriggerSourceFilter.Button, TriggerSourceFilter.Weight, TriggerSourceFilter.Api, TriggerSourceFilter.None])

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/

function parseDay(raw: string | null | undefined): string | null {
  return raw && DAY_RE.test(raw) ? raw : null
}

// `new Date('YYYY-MM-DD')` parses as UTC; build from parts so the day is local.
function localDay(day: string, offsetDays: number): Date {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(y, m - 1, d + offsetDays)
}

// Local-midnight epoch seconds for a YYYY-MM-DD string.
export function localDayStartSeconds(day: string): number {
  return localDay(day, 0).getTime() / 1000
}

// Local midnight of the *following* day — the exclusive upper bound.
export function localNextDayStartSeconds(day: string): number {
  return localDay(day, 1).getTime() / 1000
}

export function parseHashState(): HashState {
  const hash = window.location.hash

  if (hash.startsWith('#capture=')) {
    return {
      nets: new Set(),
      triage: DEFAULT_TRIAGE(),
      sessionIntent: DEFAULT_SESSION_INTENT(),
      trigger: DEFAULT_TRIGGER(),
      labels: new Set(),
      dateFrom: null,
      dateTo: null,
      captureId: decodeURIComponent(hash.slice('#capture='.length)),
      isLegacy: true,
    }
  }

  if (!hash.startsWith('#captures')) {
    return {
      nets: new Set(),
      triage: DEFAULT_TRIAGE(),
      sessionIntent: DEFAULT_SESSION_INTENT(),
      trigger: DEFAULT_TRIGGER(),
      labels: new Set(),
      dateFrom: null,
      dateTo: null,
      captureId: null,
      isLegacy: false,
    }
  }

  const qs = hash.includes('?') ? new URLSearchParams(hash.slice(hash.indexOf('?') + 1)) : null
  const nets = new Set(qs?.get('nets')?.split(',').filter(Boolean) ?? [])
  const rawTriage = qs?.get('triage')?.split(',').filter(Boolean) ?? []
  const triage = rawTriage.length > 0
    ? new Set(rawTriage as TriageResult[])
    : DEFAULT_TRIAGE()
  const rawSessionIntent = qs?.get('intent')?.split(',').filter(Boolean) ?? []
  const sessionIntent = rawSessionIntent.length > 0
    ? new Set(rawSessionIntent as SessionIntent[])
    : DEFAULT_SESSION_INTENT()
  const rawTrigger = qs?.get('trigger')?.split(',').filter(Boolean) ?? []
  const trigger = rawTrigger.length > 0
    ? new Set(rawTrigger as TriggerSourceFilter[])
    : DEFAULT_TRIGGER()
  const labels = new Set(qs?.get('labels')?.split(',').filter(Boolean) ?? [])
  const dateFrom = parseDay(qs?.get('from'))
  const dateTo = parseDay(qs?.get('to'))
  const captureId = qs?.get('capture') ?? null
  return { nets, triage, sessionIntent, trigger, labels, dateFrom, dateTo, captureId, isLegacy: false }
}

export function buildHash(opts: {
  nets: Set<string>
  triage: Set<TriageResult>
  sessionIntent: Set<SessionIntent>
  trigger: Set<TriggerSourceFilter>
  labels: Set<string>
  dateFrom: string | null
  dateTo: string | null
  captureId: string | null
}): string {
  const params = new URLSearchParams()
  if (opts.nets.size > 0) params.set('nets', Array.from(opts.nets).join(','))
  params.set('triage', Array.from(opts.triage).join(','))
  params.set('intent', Array.from(opts.sessionIntent).join(','))
  params.set('trigger', Array.from(opts.trigger).join(','))
  if (opts.labels.size > 0) params.set('labels', Array.from(opts.labels).join(','))
  if (opts.dateFrom) params.set('from', opts.dateFrom)
  if (opts.dateTo) params.set('to', opts.dateTo)
  if (opts.captureId) params.set('capture', opts.captureId)
  return '#captures?' + params.toString()
}

export function buildShareHash(c: CaptureSummary): string {
  const nets = new Set<string>()
  if (c.hostname) nets.add(c.hostname)
  else if (c.source === CaptureSource.Manual) nets.add(MANUAL_SENTINEL)

  const triage = DEFAULT_TRIAGE()
  if (c.triageResult === TriageResult.Discard) triage.add(TriageResult.Discard)

  return buildHash({ nets, triage, sessionIntent: DEFAULT_SESSION_INTENT(), trigger: DEFAULT_TRIGGER(), labels: new Set(), dateFrom: null, dateTo: null, captureId: c.captureId })
}
